import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { ProdutoPromocional } from '../../../core/models/produtos/produto-promocional';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { PrazoPagamento } from '../../../core/models/prazos-pagamento/prazo-pagamento';
import { TipoPedido } from '../../../core/models/tipos/tipo-pedido';
import { Pedido } from '../../../core/models/pedidos/pedido';
import { fatorFinanceiroItem } from '../../../core/tools/fator-financeiro-item';

/**
 * Painel de detalhe de um item do pedido (substitui a antiga tabela de 10 colunas
 * duplicada em pedido-adicionar-itens-modal.component.html:647-977, além da seção
 * "Configurar item" (linhas 978-1107) e do campo de observação (linhas 1136-1144)).
 *
 * Componente puramente apresentacional: não contém lógica de negócio própria (exceto
 * leituras puras/derivadas de `produto`, `configuracao` e `prazoPagamentoSelecionado`,
 * que são seguras de duplicar aqui pois não alteram estado — ver `ehPrecoTravado` e as
 * funções de formatação/fracionamento abaixo, copiadas verbatim do componente pai).
 * Toda ação do usuário é reemitida via @Output para o componente pai, que continua
 * detendo os métodos de negócio originais, inalterados.
 */
@Component({
  selector: 'app-pedido-itens-detalhe-painel',
  standalone: false,
  templateUrl: './pedido-itens-detalhe-painel.component.html',
  styleUrls: ['./pedido-itens-detalhe-painel.component.scss']
})
export class PedidoItensDetalhePainelComponent {
  get fatorFinanceiroExibido(): number {
    const ativo = this.configuracao?.utiliza_fator_financeiro === true
      && !this.configuracao.prazo_pagamento_antes_itenspedido && this.prazoPagamentoSelecionado != null;
    const unitario = fatorFinanceiroItem(this.produto, this.prazoPagamentoSelecionado?.fator_financeiro, ativo);
    const quantidade = Number(this.produto?.qtde);
    return Number(((unitario ?? 0) * (Number.isFinite(quantidade) && quantidade > 0 ? quantidade : 0)).toFixed(4));
  }

  get aplicaFatorReferencia(): boolean {
    return this.produto?.qtde_lote === 0 && this.configuracao != null
      && !this.configuracao.prazo_pagamento_antes_itenspedido
      && this.configuracao.utiliza_fator_financeiro && this.prazoPagamentoSelecionado != null;
  }

  @Input() produto: ProdutoPromocional = null;
  @Input() configuracao: Configuracao = null;
  @Input() prazoPagamentoSelecionado: PrazoPagamento = null;
  @Input() tiposPedidoSelecionado: TipoPedido = null;
  @Input() pedido: Pedido = null;
  @Input() exibirPontuacao = false;
  @Input() calculando = false;
  /** Constante usada pelo pai para identificar produtos de campanha (produto.categoria != campanhas). */
  @Input() campanhas = 'CAMPANHAS';
  /** Calculado pelo pai (pedido-adicionar-itens-modal.component.ts:2503) — pode depender de mais contexto que só `produto`. */
  @Input() labelPrecoTravado: string = null;

  @Input() modalFotos: TemplateRef<any> = null;
  @Input() modalInformacoes: TemplateRef<any> = null;
  @Input() modalEstoqueLocal: TemplateRef<any> = null;
  @Input() modalTabelas: TemplateRef<any> = null;
  /** Modal de seleção de lote (pedido-adicionar-itens-modal.component.ts:5409 — modalAdicionarItensLotes).
   *  Não listado no brief original, mas o gatilho real (html:987) faz parte da mesma seção
   *  "Configurar item" sendo movida para este componente; adicionado para não perder a
   *  funcionalidade de seleção de lote em produtos rastreados por lote. */
  @Input() modalLotes: TemplateRef<any> = null;

  @Output() qtdeAlterada = new EventEmitter<{ id_produto: number; foco: boolean; event?: any }>();
  @Output() qtdeIncrementada = new EventEmitter<number>();
  @Output() qtdeDecrementada = new EventEmitter<number>();
  @Output() edicaoValorIniciada = new EventEmitter<number>();
  @Output() valorDigitado = new EventEmitter<number>();
  @Output() valorValidado = new EventEmitter<{ id_produto: number; foco?: boolean; event?: any; adicionado?: boolean }>();
  @Output() valorTabelaSelecionado = new EventEmitter<{ id_produto: number; preco_unitario: number }>();
  @Output() valorComFatorSelecionado = new EventEmitter<{ id_produto: number; valorJaFatorado: number }>();
  @Output() valorLoteSelecionado = new EventEmitter<{ id_produto: number; preco_lote: number }>();
  @Output() travaRemovida = new EventEmitter<number>();
  @Output() descontoTemporarioDefinido = new EventEmitter<number>();
  @Output() edicaoDescontoIniciada = new EventEmitter<number>();
  @Output() edicaoDescontoFinalizada = new EventEmitter<{ id_produto: number; event?: any }>();
  @Output() descontoUnitarioCalculado = new EventEmitter<{ id_produto: number; alterar: boolean; adicionado: boolean }>();
  @Output() descontoSelecionado = new EventEmitter<{ id_produto: number; po_desconto: number }>();
  @Output() loteRemovido = new EventEmitter<number>();
  @Output() loteSelecionado = new EventEmitter<number>();
  @Output() bonificacaoAlterada = new EventEmitter<string>();
  @Output() abrirFotos = new EventEmitter<{ content: TemplateRef<any>; id_produto: number }>();
  @Output() abrirInformacoes = new EventEmitter<{ content: TemplateRef<any>; id_produto: number }>();
  @Output() abrirEstoqueLocal = new EventEmitter<{ content: TemplateRef<any>; id_produto: number }>();
  @Output() abrirTabelas = new EventEmitter<{ content: TemplateRef<any>; id_produto: number; id_lote: number }>();
  /** Ver comentário em `modalLotes` acima. */
  @Output() abrirLotes = new EventEmitter<{ content: TemplateRef<any>; id_produto: number }>();

  /**
   * Corpo real verificado em pedido-adicionar-itens-modal.component.ts:2497-2501.
   * Leitura pura de `produto` — duplicada aqui (em vez de round-trip por @Output)
   * porque o template a usa em várias condições *ngIf a cada render.
   */
  ehPrecoTravado(produto: ProdutoPromocional): boolean {
    if (produto == null) { return false; }
    if (produto.estado_preco != null) { return produto.estado_preco.travado === true; }
    return produto.preco_tabela_travado === true;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:5136-5163. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:5136 — keep in sync if that formula changes.
  formatarValoresQuatroCasasDecimais(valor: number, porcentagem?: boolean, zero?: boolean): string {
    if (valor != null) {
      if (porcentagem) {
        return (valor).toLocaleString('pt-BR', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      } else {
        return (valor).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }
    } else {
      if (zero) {
        return (0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        });
      }

      return null;
    }
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:6368-6374. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:6368 — keep in sync if that formula changes.
  precoFracionadoReferencia(produto: ProdutoPromocional): number {
    if (produto == null) { return 0; }
    const base: number = this.valorProdutoParaFracionado(produto);
    if (base <= 0) { return 0; }
    const litragem: number = this.litragemProduto(produto);
    return litragem > 0 ? base / litragem : base;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:6376-6387. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:6376 — keep in sync if that formula changes.
  precoFracionadoComFatorReferencia(produto: ProdutoPromocional): number {
    if (produto == null) { return 0; }
    let base: number = produto.valor_fator_financeiro_tabela != null && produto.valor_fator_financeiro_tabela > 0 ? Number(produto.valor_fator_financeiro_tabela) : 0;
    if (base <= 0) {
      const bruto: number = this.valorProdutoParaFracionado(produto);
      const fator: number = this.fatorFinanceiroAtual(produto);
      base = fator !== 0 ? bruto * (1 + (fator / 100)) : bruto;
    }
    if (base <= 0) { return 0; }
    const litragem: number = this.litragemProduto(produto);
    return litragem > 0 ? base / litragem : base;
  }

  /** Mensagem de erro de quantidade — deriva a string real a partir de pedido-adicionar-itens-modal.component.html:1009-1017. */
  mensagemErroQtde(produto: ProdutoPromocional): string {
    if (produto == null || !produto.erro_qtde) { return null; }
    if (produto['qtde_minima_tabela'] != null && produto['qtde_minima_tabela'] > 0) {
      return 'Qtde mínima da tabela: ' + produto['qtde_minima_tabela'];
    }
    return 'Qtde inválida!';
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:2519-2529. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:2519 — keep in sync if that formula changes.
  private fatorFinanceiroAtual(produto: ProdutoPromocional): number {
    if (produto == null || !produto.aplica_fator_financeiro) { return 0; }
    if (this.configuracao == null
        || this.configuracao.prazo_pagamento_antes_itenspedido
        || !this.configuracao.utiliza_fator_financeiro) { return 0; }
    if (this.prazoPagamentoSelecionado == null || this.prazoPagamentoSelecionado.fator_financeiro == null) { return 0; }

    const fator = Number(this.prazoPagamentoSelecionado.fator_financeiro);

    return isFinite(fator) ? fator : 0;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:6360-6366. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:6360 — keep in sync if that formula changes.
  private valorProdutoParaFracionado(produto: ProdutoPromocional): number {
    if (produto == null) { return 0; }
    if (produto.price != null && produto.price > 0) { return Number(produto.price); }
    if (produto.valor != null && produto.valor > 0) { return Number(produto.valor); }
    if (produto.valor_tabela != null && produto.valor_tabela > 0) { return Number(produto.valor_tabela); }
    return 0;
  }

  /** Corpo real verificado em pedido-adicionar-itens-modal.component.ts:6356-6358. */
  // Mirrors pedido-adicionar-itens-modal.component.ts:6356 — keep in sync if that formula changes.
  private litragemProduto(produto: ProdutoPromocional): number {
    return produto != null && produto.valor_litragem != null && produto.valor_litragem > 0 ? Number(produto.valor_litragem) : 0;
  }
}
